import type { AxiosInstance } from 'axios';
import type { ServiceResult, ServiceResultT } from '../types/common';
import type {
  NotificationViewModel,
  NotificationCategory,
  UnreadCountsByCategory,
} from '../types/notification';
import { apiClient } from '../client/client';

/**
 * Service for user notification endpoints.
 * Separate from NotificationTypeService which is the admin CRUD for notification templates.
 */
export class NotificationService {
  private static readonly BASE = '/api/notification';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(category?: NotificationCategory): Promise<ServiceResultT<NotificationViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<NotificationViewModel[]>>(
      NotificationService.BASE,
      { params: category ? { category } : undefined },
    );
    return data;
  }

  async getUnreadCount(): Promise<ServiceResultT<number>> {
    const { data } = await this.client.get<ServiceResultT<number>>(
      `${NotificationService.BASE}/unread-count`,
    );
    return data;
  }

  async getUnreadCounts(): Promise<ServiceResultT<UnreadCountsByCategory>> {
    const { data } = await this.client.get<ServiceResultT<UnreadCountsByCategory>>(
      `${NotificationService.BASE}/unread-counts`,
    );
    return data;
  }

  async markAsRead(id: string): Promise<ServiceResult> {
    const { data } = await this.client.put<ServiceResult>(
      `${NotificationService.BASE}/${id}/read`,
    );
    return data;
  }

  async markAllAsRead(category?: NotificationCategory): Promise<ServiceResult> {
    const { data } = await this.client.put<ServiceResult>(
      `${NotificationService.BASE}/read-all`,
      null,
      { params: category ? { category } : undefined },
    );
    return data;
  }

  async markReadForConversation(conversationId: string): Promise<ServiceResultT<number>> {
    const { data } = await this.client.put<ServiceResultT<number>>(
      `${NotificationService.BASE}/read-by-conversation/${conversationId}`,
    );
    return data;
  }
}

export const notificationService = new NotificationService();
